import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { getUser } from '../../utils/tokens';
import Toggle from '../../utils/Toggle';
import { ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import { submitFileAsset } from '../../utils/FileHelper';
import Spinner from '../../utils/Spinner';
import { filter, find, indexOf } from 'rambda';
import DeleteConfirm from '../../utils/DeleteConfirm';
import { toast, ToastContainer } from 'react-toastify';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import InlineEditor from 'ckeditor5-custom-build/build/ckeditor';
import SelectBox from '../../utils/SelectBox';
import DropUpload from '../../utils/Dropupload';

const assetUploadURL = '/resource/v1/resource-categories/files';
const assetDeleteURL = id => `/resource/v1/resources/attachment/${id}`;
const createUrl = clientId => `/resource/v1/resource-categories/clients/${clientId}`;
const getCategoryUrl = (categoryId, clientId) => `/resource/v1/resource-categories/${categoryId}/clients/${clientId}`;
const updateUrl = (categoryId, clientId) => `/resource/v1/resource-categories/${categoryId}/clients/${clientId}`;
const deleteURL = id => `/resource/v1/resource-categories/${id}`;
const getImgUrl = id => `/resource/v1/resources/${id}/file/${id}`;
const listUrl = clientId => `resource/v1/resource-categories/clients/${clientId}`;

/**
 *
 * @param category
 * @param setCategory
 * @returns {JSX.Element}
 * @constructor
 */
const CategoryCreate = ({ category, setCategory }) => {
	let navigate = useNavigate();
	const form = useRef(null);
	const parentRef = useRef(null);

	const [title, setTitle] = useState('');
	const [description, setDescription] = useState(' ');
	const [enabled, setEnabled] = useState(true);
	const [global, setGlobal] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [document, setDocument] = useState(null);
	const [documentLabel, setDocumentLabel] = useState('');
	const [parentCategory, setParentCategory] = useState(null);
	const [categories, setCategories] = useState(null);
	const [valid, setValid] = useState(false);
	const [imgUrl, setImgUrl] = useState();
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [editor, setEditor] = useState(null);

	const currentUser = getUser();

	useEffect(loadCategories, []);

	useEffect(() => {
		if (category) {
			setEnabled(category.enabled);
			setGlobal(category.global);
			setTitle(category.title);

			if (category.description) {
				setDescription(category.description);
			} else {
				setDescription(' ');
			}
			if (category.imageId) {
				setDocument({ id: category.imageId });
			}
		} else {
			if (currentUser.scope.includes('admin')) {
				setGlobal(true);
				setEnabled(true);
			}
		}
	}, [category, categories]);

	useEffect(() => {
		if (document) {
			getImage(document.id);
		}
	}, [document]);

	useEffect(() => {
		setEditor(
			<CKEditor
				editor={InlineEditor}
				data={description}
				onChange={(event, editor) => {
					const data = editor.getData();
					setDescription(data);
				}}
			/>,
		);
	}, [description]);

	function loadCategories() {
		const getCategories = async () => {
			let url = listUrl(currentUser.clientIdNum);
			url = global ? url + '?global=true' : url;

			const response = await axios.get(url);

			if (response.status !== 200) {
				setCategories([{ id: -1, title: 'No categories' }]);
			} else {
				response.data.unshift({ id: -1, title: 'No parent category' });
				if (category && category.global) {
					const filtered = filter(c => {
						return c.global;
					}, response.data);

					filterSelf(filtered);
				} else if (category && !global) {
					const filtered = filter(c => {
						return !c.global || c.id === -1;
					}, response.data);
					filterSelf(filtered);
				} else {
					filterSelf(response.data);
				}
			}
		};

		const filterSelf = cats => {
			setParentCategory({ id: -1, title: 'No parent category' });
			if (category) {
				const parentCat = find(c => {
					return category.parentCategoryId === c.id;
				}, cats);

				if (parentCat) {
					setParentCategory(parentCat);
				}

				const filtered = filter(c => {
					return c.id !== category.id && c.parentCategoryId === null;
				}, cats);

				setCategories(filtered);
			} else {
				const filtered = filter(c => {
					return c.parentCategoryId === null;
				}, cats);
				setCategories(filtered);
			}
		};

		// const ro = new ResizeObserver(entries => {
		// 	entries.forEach(entry => {
		// 		console.log('resize'); // zzmatt
		// 		console.log(entry); // zzmatt
		// 		const message = { height: entry.contentRect.height };
		// 		window.parent.postMessage(message, 'https://app.local.etw.com:1338');
		// 	});
		// });
		//
		// ro.observe(parentRef.current);

		getCategories().then();

		//return () => ro.disconnect();
	}

	function titleChange(e) {
		setTitle(e.target.value);
		verifyForm();
	}

	function verifyForm() {
		if (title && title.length > 0) {
			setValid(true);
		}
	}

	function getImage(id) {
		axios({
			method: 'GET',
			url: getImgUrl(id),
		}).then(
			response => {
				setImgUrl(response.data);
			},
			error => {
				console.log(error);
			},
		);
	}

	function handleSubmitClick(e) {
		e.preventDefault();

		let theUrl = category ? updateUrl(category.id, currentUser.clientIdNum) : createUrl(currentUser.clientIdNum);
		let theMethod = category ? 'PATCH' : 'POST';

		const data = {
			title: title,
			description: description,
			enabled: enabled,
			global: global,
		};

		if (parentCategory && parentCategory.id !== -1) {
			data.parentCategoryId = parentCategory.id;
		}

		if (document) {
			data.imageId = document.id;
		}

		axios({
			method: theMethod,
			url: theUrl,
			data: data,
		}).then(
			response => {
				navigate(-1);
			},
			error => {
				console.log(error);
			},
		);
	}

	function handleDeleteClick() {
		setShowDeleteConfirm(true);
	}

	const confirmDelete = () => {
		axios({
			method: 'DELETE',
			url: deleteURL(category.id),
		}).then(
			() => {
				toast('Category deleted', {
					autoClose: 3000,
					type: toast.TYPE.SUCCESS,
				});
				navigate(-1);
			},
			error => {
				let msg = 'Category not deleted';
				if (error.response.status === 409) {
					msg =
						'This category cannot be deleted because it contains subcategories. All subcategories must be moved or deleted prior to deleting a parent category';
				}
				toast(msg, {
					autoClose: 10000,
					type: toast.TYPE.ERROR,
				});
			},
		);
	};

	function submitFile(e) {
		submitFileAsset({
			assetUploadURL: assetUploadURL,
			e: e,
			documentLabel: documentLabel,
			setUploading: setUploading,
			setDocument: setDocument,
			setDocumentLabel: setDocumentLabel,
			verifyForm: verifyForm,
		}).then(response => {
			console.log(response.data); // zzmatt
		});
	}

	const handleAttachmentTrash = async e => {
		await axios({
			method: 'DELETE',
			url: assetDeleteURL(document.id),
		});
		setDocument(null);
		setDocumentLabel('');
	};

	function handleCancelClick() {
		navigate(-1);
	}

	function handleBack() {
		navigate(-1);
	}

	const style = {
		overflow: 'auto',
		maxHeight: 700,
	};

	return (
		<>
			<ToastContainer />
			{showDeleteConfirm && (
				<>
					<DeleteConfirm
						open={showDeleteConfirm}
						title="Delete Category"
						text={title}
						setOpen={setShowDeleteConfirm}
						confirm={confirmDelete}
					/>
				</>
			)}
			<div className="py-4 px-4 space-y-8" style={style} ref={parentRef}>
				<div className="pb-4" onClick={handleBack}>
					<span className="text-indigo-600">
						<ArrowUturnLeftIcon className="h-6 w-6 text-indigo-600" />
					</span>
				</div>
				<div>
					<h3 className="text-lg font-medium leading-6 text-gray-900">Category Details</h3>
				</div>
				<form ref={form}>
					<div className="mt-6 grid grid-cols-6 gap-y-6 gap-x-4">
						<div className="sm:col-span-2 col-start-1 col-end-3">
							<label htmlFor="input_qb_id" className="block text-sm font-medium text-gray-700">
								Category Title
							</label>
							<input
								required
								type="text"
								value={title}
								className="block w-full min-w-0 flex-1 rounded-none rounded-l-md rounded-r-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm invalid:border-red-500"
								id="title"
								onChange={titleChange}
								tabIndex="1"
							/>
						</div>
						<div className="col-start-1 col-end-5">
							<label htmlFor="input_qb_id" className="block text-sm font-medium text-gray-700">
								Category Description
							</label>

							<div className="rounded-none rounded-l-md rounded-r-md border-2 bg-white">{editor}</div>
						</div>
						<div className="col-start-1 col-end-4">
							{categories && (
								<SelectBox
									label="Select Parent Category"
									choices={categories}
									selected={parentCategory}
									setSelected={setParentCategory}
								/>
							)}
						</div>
						{parentCategory && parentCategory.id > 0 && (
							<div className="xs:col-span-10 col-start-1 col-end-3">
								<label className="block text-sm font-medium text-gray-700">Category Image</label>
								{document && imgUrl && (
									<div className="py-4 mt-1 flex justify-center rounded-md border-2 border-dashed border-gray-300 px-4 pt-5 pb-5">
										<div className="group aspect-w-2 aspect-h-1 overflow-hidden rounded-lg sm:aspect-none sm:relative sm:h-full">
											<img src={imgUrl} alt="Category" className="object-fill h-48 w-96 ..." />
											<div
												aria-hidden="true"
												className="bg-gradient-to-b from-transparent to-black opacity-50 sm:absolute sm:inset-0"
											/>
											<div className="flex items-end p-6 sm:absolute sm:inset-0">
												<div>
													<h3 className="font-semi-bold text-white">{title}</h3>
												</div>
											</div>
										</div>
									</div>
								)}
								{!document && !uploading && (
									<DropUpload
										titleText="Upload an image for the category"
										fileText="PNG, JPG, GIF up to 2MB"
										submitFile={submitFile}
										maxSize="2000000"
									/>
								)}
							</div>
						)}
						{uploading && (
							<>
								<div className="py-4 grid grid-cols-3">
									<div>
										<Spinner />
									</div>
								</div>
							</>
						)}
						{document && !uploading && (
							<>
								<div className="sm:col-span-3 col-start-1 col-end-3"></div>
								<div className="pt-0 grid grid-cols-3">
									<div className="justify-self-start" onClick={handleAttachmentTrash}>
										<svg
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 24 24"
											strokeWidth={1.5}
											stroke="currentColor"
											className="w-6 h-6">
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
											/>
										</svg>
									</div>
								</div>
							</>
						)}

						<div className="col-start-1 col-end-4">
							<Toggle
								label="Enabled"
								value={enabled}
								onChangeValue={e => {
									setEnabled(e);
								}}
							/>
						</div>

						{currentUser.scope && indexOf('admin', currentUser.scope) >= 0 && (
							<>
								<div className="col-start-1 col-end-4">
									<Toggle
										label="Global"
										value={global}
										onChangeValue={e => {
											setGlobal(e);
										}}
									/>
								</div>
							</>
						)}
					</div>
					<div className="pt-5">
						<div className="px-0">
							{category && (
								<button
									onClick={() => {
										handleDeleteClick();
									}}
									type="button"
									className="m-1 rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
									Delete
								</button>
							)}
							<button
								onClick={() => {
									handleCancelClick();
								}}
								type="button"
								className="m-1 rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
								Cancel
							</button>
							{(valid || category) && (
								<button
									onClick={e => {
										handleSubmitClick(e);
									}}
									className="ml-1 inline-flex justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
									Save
								</button>
							)}
						</div>
					</div>
				</form>
			</div>
		</>
	);
};

/**
 *
 * @returns {JSX.Element}
 * @constructor
 */
const LoadingCategory = () => {
	return <div>Loading category</div>;
};

/**
 *
 * @returns {JSX.Element}
 * @constructor
 */
const CategoryCreatePage = () => {
	const [category, setCategory] = useState(null);

	const currentUser = getUser();
	let { categoryId } = useParams();

	useEffect(() => {
		const fetchResource = async () => {
			const response = await axios({
				method: 'GET',
				url: getCategoryUrl(categoryId, currentUser.clientIdNum),
			});

			setCategory(response.data);
		};

		if (categoryId) {
			fetchResource().catch(console.error);
		}
	}, [categoryId]);

	if (categoryId && !category) {
		return <LoadingCategory />;
	}

	if (!categoryId) {
		return <CategoryCreate />;
	}

	if (category) {
		return <CategoryCreate category={category} setCategory={setCategory} />;
	}
};

export default CategoryCreatePage;
